import { Box, Tooltip, styled } from '@mui/material'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import DirectionsWalkOutlinedIcon from '@mui/icons-material/DirectionsWalkOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import type { ReactNode } from 'react'

import {
  RECORDING_NAV_BLOCK_HINT,
  useActiveRecording,
} from '@/apps/latest/shared/contexts/ActiveRecordingContext'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { useReleaseChannel } from '@/features/auth'
import { RADIUS } from '@/shared/styles/radii'
import { ICON_SIZE } from '@/shared/styles/typography'

export type IconKey =
  | 'home'
  | 'homecare'
  | 'messages'
  | 'knowledge'
  | 'settings'

interface Props {
  active: IconKey
  onChange: (key: IconKey) => void
  // 出すタブ。省略時は全部 (β 判定は従来どおり)。薬剤師の左ペインは LINE 連携の有無で「やり取り」を出し分ける
  keys?: readonly IconKey[]
  // 見ていないものがあるタブ。右上にドットを出す (数字は出さない。design-book ADR-0052)
  unreadKeys?: readonly IconKey[]
}

interface IconItem {
  key: IconKey
  label: string
  icon: ReactNode
  // release_channel が β 以上のユーザーにのみ出す (= β 機能。導線を隠すだけの制御)
  betaOnly?: boolean
  // release_channel が α のユーザーにのみ出す (= 共同開発版の機能。導線を隠すだけの制御)
  alphaOnly?: boolean
}

// icon は Notion 原典と同じ outline で揃える (icons.md: Outlined 優先 + バー内で variant 統一)。
// 塗りは 18px でシルエットが黒い塊になり、Notion の細い線の感触から外れる。
const items: IconItem[] = [
  {
    key: 'home',
    label: 'ホーム',
    icon: <HomeOutlinedIcon sx={{ fontSize: ICON_SIZE.lg }} />,
  },
  {
    // 在宅の患者 (登録した人だけ) と、その患者の宛先・訪問。在宅の録音はここの患者のページから始める。
    // icon は「そこで何をするか」= 訪問する (歩く人)。家はホームと読み違えるので使わない。
    key: 'homecare',
    label: '在宅',
    icon: <DirectionsWalkOutlinedIcon sx={{ fontSize: ICON_SIZE.lg }} />,
    alphaOnly: true,
  },
  {
    // 患者から LINE で届いたメッセージ。外から届くものを受ける場所なので
    // Notion 原典の Inbox と同じ形 (トレイ) にする。マニュアルの吹き出しとも見分けがつく。
    key: 'messages',
    label: 'やり取り',
    icon: <InboxOutlinedIcon sx={{ fontSize: ICON_SIZE.lg }} />,
  },
  {
    // 社内マニュアル・行政通知への照会。録音/薬歴とは別の作業であり、
    // 設定の下位項目ではなくホームと同格のタブに置く。
    // icon は 28 spec の語彙 message-circle (= 問う場所) に対応させる。この軸の仕事は
    // 資料を眺めることではなく資料に問うことなので、資料 (file-text) より問い (吹き出し)
    // の方が行き先を正しく指す (Notion AI の Chat タブと同じ形)。
    key: 'knowledge',
    label: 'マニュアル',
    icon: <ChatBubbleOutlineIcon sx={{ fontSize: ICON_SIZE.lg }} />,
    betaOnly: true,
  },
  {
    key: 'settings',
    label: '設定',
    icon: <SettingsOutlinedIcon sx={{ fontSize: ICON_SIZE.lg }} />,
  },
]

const Bar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  height: 44,
  padding: '0 12px',
})

const IconButton = styled(Box, {
  shouldForwardProp: (p) => p !== 'active' && p !== 'disabled',
})<{ active: boolean; disabled?: boolean }>(({ active, disabled }) => ({
  position: 'relative',
  width: 32,
  height: 32,
  borderRadius: RADIUS.lg,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.4 : 1,
  background: active ? colorTokens.bg.muted : 'transparent',
  color: active ? colorTokens.fg.default : colorTokens.fg.muted,
  transition: 'background-color 120ms, color 120ms',
  '&:hover':
    active || disabled ? undefined : { background: colorTokens.bg.emphasis },
}))

// 未読ドット: 6px・fg.default の塗り。アイコン (18px) は中央なので右上の余白に収まる
const UnreadDot = styled(Box)({
  position: 'absolute',
  top: 4,
  right: 4,
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: colorTokens.fg.default,
})

export const IconBar = ({ active, onChange, keys, unreadKeys }: Props) => {
  const { isActiveRecording } = useActiveRecording()
  const { isBeta, isAlpha } = useReleaseChannel()
  const visibleItems = items.filter(
    (it) =>
      (!keys || keys.includes(it.key)) &&
      (!it.betaOnly || isBeta) &&
      (!it.alphaOnly || isAlpha)
  )
  return (
    <Bar role="tablist">
      {visibleItems.map((it) => (
        <Tooltip
          key={it.key}
          title={isActiveRecording ? RECORDING_NAV_BLOCK_HINT : it.label}
          placement="bottom"
        >
          <IconButton
            active={it.key === active}
            disabled={isActiveRecording}
            onClick={() => {
              if (isActiveRecording) return
              onChange(it.key)
            }}
            role="tab"
            aria-selected={it.key === active}
            aria-disabled={isActiveRecording}
            aria-label={it.label}
          >
            {it.icon}
            {unreadKeys?.includes(it.key) ? (
              <UnreadDot data-testid={`unread-dot-${it.key}`} />
            ) : null}
          </IconButton>
        </Tooltip>
      ))}
    </Bar>
  )
}
